import Link from "next/link";
import { useEffect, useRef } from "react";
import {
  Search,
  Plus,
  Database,
  ChartColumn,
  Settings2,
  LayoutGrid,
  History,
  BookOpen,
} from "lucide-react";
import { useSetAtom } from "jotai";
import {
  IEntry,
  SIDENAV_GROUP_ACTION_CLASS,
  sectionEntry,
} from "./sidenavShared";
import {
  isSectionHrefActive,
  useIsGateAllowed,
} from "@/components/Layouts/pageSections";
import { isCommandPaletteOpenAtom } from "@/store/ui-atoms";
import { useSpotUser } from "@/utils/spotUser";
import { isCaseManagementEnabled, isEditorV2Enabled } from "@/utils/modules";
import { usePathname, useSearchParams } from "next/navigation";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { SideNavShell } from "./SideNavShell";
import {
  EntryIcon,
  EntryLabel,
  SideNavExpandableEntry,
  SideNavSubEntry,
} from "./SideNavEntry";
import { Button } from "@/components/shadcn/button";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/shadcn/sidebar";
import { InboxList } from "../Inbox/InboxList";
import { AddInboxDialog } from "../Inbox/AddInboxDialog";
import { ImportInboxButton } from "../Inbox/ImportInboxButton";
import { UpgradeSection } from "./UpgradeSection";
import { cn } from "@/lib/utils";
import { Kbd } from "@/components/shadcn/kbd";
import { isMac } from "@/utils/generic";
import { NoxusLoadingScreen } from "@/components/UI/NoxusLoadingScreen";
import { useInboxes } from "../Inbox/useInboxes";
import { usePermissions } from "@/hooks/usePermissions";
import {
  APP_LIBRARY_PERMS,
  CASES_PERMS,
  MANAGE_WORKSPACE_PERMS,
  PAST_USAGE_PERMS,
  WORKSPACE_PERMISSIONS,
  P,
} from "@/constants/permissions";

// Browsing the Workshop list requires an actual view grant — resource:run
// alone is for the App library (run apps), not for inspecting the
// underlying flow/agent/KB definitions.
const RESOURCE_VIEW_PERMS = [
  P.RESOURCE_READ,
  P.RESOURCE_EDIT,
  P.RESOURCE_DELETE,
];

const WORKSHOP_GATE: Partial<IEntry> = {
  requiredPermissions: RESOURCE_VIEW_PERMS,
  level: "group",
};
const PAST_USAGE_GATE: Partial<IEntry> = {
  requiredPermissions: PAST_USAGE_PERMS,
  level: "group",
};

const ENTRIES_BUILD: IEntry[] = [
  sectionEntry("workshop", "flows", WORKSHOP_GATE),
  sectionEntry("workshop", "agents", WORKSHOP_GATE),
  {
    title: "Data",
    icon: Database,
    id: "data",
    children: [
      sectionEntry("workshop", "knowledge-bases", WORKSHOP_GATE),
      sectionEntry("workshop", "tables", WORKSHOP_GATE),
    ],
  },
  {
    title: "Templates",
    icon: BookOpen,
    id: "templates",
    href: "/templates",
    requiredPermissions: [WORKSPACE_PERMISSIONS.EDIT],
    level: "group",
  },
  {
    title: "App library",
    icon: LayoutGrid,
    id: "apps",
    href: "/apps",
    requiredPermissions: APP_LIBRARY_PERMS,
    level: "group",
  },
];

const ENTRIES_GOVERNANCE: IEntry[] = [
  {
    title: "Past usage",
    icon: History,
    id: "past-usage",
    children: [
      sectionEntry("pastUsage", "flows", PAST_USAGE_GATE),
      sectionEntry("pastUsage", "coworkers", PAST_USAGE_GATE),
    ],
  },
  sectionEntry("workspace", "analytics", { icon: ChartColumn }),
  sectionEntry("workspace", "linked-accounts"),
];

const TAB_HREFS = [...ENTRIES_BUILD, ...ENTRIES_GOVERNANCE]
  .flatMap((entry) => entry.children ?? [entry])
  .flatMap((entry) => (entry.href?.includes("tab=") ? [entry.href] : []));

const PLATFORM_GROUPS = [
  { label: "Build", entries: ENTRIES_BUILD },
  { label: "Governance", entries: ENTRIES_GOVERNANCE },
];

const ENTRIES_BOTTOM: IEntry[] = [
  {
    title: "Manage workspace",
    icon: Settings2,
    id: "settings",
    href: "/manage-workspace",
    requiredPermissions: MANAGE_WORKSPACE_PERMS,
    level: "group",
  },
];

export const DashboardSideNav = () => {
  const { spotUser, spotGroup } = useSpotUser();
  const editorV2Enabled = isEditorV2Enabled(spotGroup);
  const {
    isLoading: isLoadingPermissions,
    hasPermission,
    hasAnyPermission,
  } = usePermissions();
  const canSeeInboxes =
    isCaseManagementEnabled(spotGroup) &&
    hasAnyPermission(CASES_PERMS, "group");
  const { inboxes, isLoading: isLoadingInboxes } = useInboxes(canSeeInboxes);
  const canManageInboxes = hasPermission(
    WORKSPACE_PERMISSIONS.CASES.MANAGE_INBOXES,
    "group"
  );

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const sidebarContentRef = useRef<HTMLDivElement>(null);
  const setIsCommandPaletteOpen = useSetAtom(isCommandPaletteOpenAtom);

  const currentTab = searchParams?.get("tab");

  const isTabHrefActive = (href: string) =>
    isSectionHrefActive(href, pathname, currentTab);

  const getIsActive = (href: string | undefined) => {
    if (!href) return false;

    if (href.includes("tab=")) return isTabHrefActive(href);
    // A page whose current tab has its own entry (Analytics on Workspace
    // control) lights that entry, not the page's.
    if (TAB_HREFS.some(isTabHrefActive)) return false;

    return pathname?.includes(href);
  };

  const isEntryAllowed = useIsGateAllowed();

  const renderExpandable = (entry: IEntry, children: IEntry[]) => {
    const pages = children.filter(isEntryAllowed);
    if (pages.length === 0) return null;
    return (
      <SideNavExpandableEntry
        key={entry.id}
        title={entry.title}
        icon={entry.icon}
        hasActiveChild={pages.some((page) => !!getIsActive(page.href))}
      >
        {pages.map((page) => (
          <SideNavSubEntry
            key={page.id}
            title={page.title}
            href={page.href ?? "#"}
            isActive={!!getIsActive(page.href)}
          />
        ))}
      </SideNavExpandableEntry>
    );
  };

  useEffect(() => {
    const content = sidebarContentRef.current;
    if (!content) return;

    const preventHorizontalScroll = () => {
      if (content.scrollLeft !== 0) {
        content.scrollLeft = 0;
      }
    };

    content.addEventListener("scroll", preventHorizontalScroll, {
      passive: true,
    });

    return () => {
      content.removeEventListener("scroll", preventHorizontalScroll);
    };
  }, []);

  if (isLoadingPermissions) {
    return <NoxusLoadingScreen />;
  }

  return (
    <SideNavShell
      header={<WorkspaceSwitcher />}
      testIdPrefix="sidenav"
      contentRef={sidebarContentRef}
    >
      <SidebarGroup className="py-0 px-2">
        <SidebarMenu className="list-none">
          <SidebarMenuItem>
            <SidebarMenuButton
              data-testid="sidenav-search"
              onClick={() => setIsCommandPaletteOpen(true)}
              className="group/menu-button cursor-pointer bg-muted/70 hover:bg-muted"
            >
              <EntryIcon icon={Search} />
              <EntryLabel>Global search</EntryLabel>
              <div className="ml-auto">
                <Kbd variant="hint">{isMac() ? "⌘K" : "Ctrl+K"}</Kbd>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>

      {canSeeInboxes && (
        <SidebarGroup
          className={cn(
            "py-0 px-2",
            // With no inboxes there is nothing to show on the icon rail.
            !isLoadingInboxes &&
              inboxes.length === 0 &&
              "group-data-[collapsible=icon]:hidden"
          )}
        >
          {/* To add the plus icon we need to do this mini bungada sorry */}
          <SidebarGroupLabel className="justify-between group/label pr-0">
            <span>Inboxes</span>
            <div className="mr-0.5 ml-auto flex items-center gap-0.5">
              {canManageInboxes && (
                <>
                  <ImportInboxButton />
                  <AddInboxDialog
                    trigger={
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Add inbox"
                        className={SIDENAV_GROUP_ACTION_CLASS}
                      >
                        <Plus className="size-4" strokeWidth={2} />
                      </Button>
                    }
                  />
                </>
              )}
            </div>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <InboxList />
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {PLATFORM_GROUPS.map(({ label, entries }) => {
        // Drop entries the current user can't see; if nothing remains
        // skip the whole group so we don't render a dangling header
        // above an empty list.
        const visibleEntries = entries.filter((entry) => {
          if (entry.id === "templates" && editorV2Enabled) return false;
          if (entry.children)
            return entry.children.some((child) => isEntryAllowed(child));
          return isEntryAllowed(entry);
        });
        if (visibleEntries.length === 0) return null;
        // Someone who can run apps but not view tools (resource:run alone)
        // gets only the App library here, and there is nothing to build.
        const isAppsOnly = visibleEntries.every((entry) => entry.id === "apps");
        return (
          <SidebarGroup key={label} className="py-0 px-2">
            <SidebarGroupLabel>{isAppsOnly ? "Apps" : label}</SidebarGroupLabel>

            <SidebarGroupContent>
              <SidebarMenu className="list-none gap-0.5">
                {visibleEntries.map((entry) => {
                  if (entry.children) {
                    return renderExpandable(entry, entry.children);
                  }
                  const isActive = getIsActive(entry.href);
                  return (
                    <SidebarMenuItem key={entry.id} className="list-none">
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        className="group/menu-button"
                      >
                        <Link
                          href={entry.href ?? "#"}
                          className="flex items-center w-full"
                        >
                          <EntryIcon icon={entry.icon} />
                          <EntryLabel>{entry.title}</EntryLabel>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        );
      })}

      <SidebarGroup className="mt-auto px-2">
        <SidebarGroupContent>
          <SidebarMenu className="list-none gap-0.5">
            <UpgradeSection />
            {ENTRIES_BOTTOM.map((entry) => {
              const isActive = getIsActive(entry.href);

              if (!isEntryAllowed(entry)) {
                return null;
              }
              if (entry.children) {
                return renderExpandable(entry, entry.children);
              }

              return (
                <SidebarMenuItem key={entry.id} className="list-none">
                  <SidebarMenuButton
                    asChild
                    isActive={isActive}
                    className="group/menu-button"
                  >
                    <Link
                      href={entry.href ?? "#"}
                      className="flex items-center w-full"
                    >
                      <EntryIcon icon={entry.icon} />
                      <EntryLabel>{entry.title}</EntryLabel>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SideNavShell>
  );
};
